import { useEffect, useState } from 'react';
import { create } from 'zustand';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastStore {
  toasts: Toast[];
  add: (message: string, type: ToastType) => void;
  remove: (id: string) => void;
}

const TOAST_DURATION_MS = 5000;

export const useToastStore = create<ToastStore>((set) => ({
  toasts: [],

  add: (message, type) => {
    const id = Math.random().toString(36).slice(2);
    set((state) => ({
      toasts: [...state.toasts, { id, message, type }],
    }));

    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    }, TOAST_DURATION_MS);
  },

  remove: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },
}));

/**
 * Shows a success toast.
 */
export function showSuccess(message: string): void {
  useToastStore.getState().add(message, 'success');
}

/**
 * Shows an error toast.
 */
export function showError(message: string): void {
  useToastStore.getState().add(message, 'error');
}

/**
 * Shows an info toast.
 */
export function showInfo(message: string): void {
  useToastStore.getState().add(message, 'info');
}

/**
 * Toast container component.
 */
export function ToastContainer() {
  const { toasts, remove } = useToastStore();

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-3 pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={() => remove(toast.id)} />
      ))}
    </div>
  );
}

interface ToastItemProps {
  toast: Toast;
  onClose: () => void;
}

/**
 * Individual toast notification.
 */
function ToastItem({ toast, onClose }: ToastItemProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    const visibilityTimer = setTimeout(() => setIsVisible(true), 10);
    const progressFrame = requestAnimationFrame(() => setProgress(0));

    return () => {
      clearTimeout(visibilityTimer);
      cancelAnimationFrame(progressFrame);
    };
  }, []);

  const bgColors = {
    success: 'from-emerald-500/70 to-emerald-600/70',
    error: 'from-red-500/70 to-red-600/70',
    info: 'from-primary-500/70 to-primary-600/70',
  };

  return (
    <div
      className={`
        glass pointer-events-auto text-white px-4 py-3 rounded-2xl shadow-card
        flex items-center gap-3 min-w-[300px] max-w-[420px]
        transition-all duration-300 animate-slide-in-right
        ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}
      `}
    >
      <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${bgColors[toast.type]} flex items-center justify-center`}>
        <ToastIcon type={toast.type} />
      </div>
      <span className="flex-1">{toast.message}</span>
      <button
        onClick={onClose}
        className="p-1 hover:bg-white/10 rounded transition-colors"
      >
        <CloseIcon />
      </button>
      <div className="absolute bottom-0 left-0 h-0.5 w-full bg-white/10 overflow-hidden rounded-b-2xl">
        <div
          className="h-full bg-white/60"
          style={{ width: `${progress}%`, transition: `width ${TOAST_DURATION_MS}ms linear` }}
        />
      </div>
    </div>
  );
}

/**
 * Icon selection for toast types.
 */
function ToastIcon({ type }: { type: ToastType }) {
  if (type === 'error') {
    return (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.99-1.742 2.99H3.48c-1.53 0-2.492-1.656-1.743-2.99L8.257 3.1zm.743 4.4a1 1 0 012 0v4a1 1 0 01-2 0v-4zm1 8a1.25 1.25 0 100-2.5 1.25 1.25 0 000 2.5z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  if (type === 'success') {
    return (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
    );
  }

  return (
    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 10-2 0v4a1 1 0 001 1h1a1 1 0 100-2v-3z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Close icon for toast dismiss actions.
 */
function CloseIcon() {
  return (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}
